import { formatPhoneNumber } from "utils/common";

import { PREFERRED_CHANNEL_TYPES } from "../../../../constants/suno-comms";
import type { ContactFull } from "services/suno-comms/types/lead-contact.type";
import {
  LEAD_MERGE_TAG_KEYS,
  LEGACY_LEAD_CONTACT_OPTIONS
} from "../merge-tags-options/lead-contact.utils";
import { getFormattedDate } from "../merge-tags.utils";
import type { MergeTagsReplaceHandler } from "./merge-tags-values";

type LeadContactHandler = MergeTagsReplaceHandler[string];

const getLeadContactParam = (
  contact: ContactFull | undefined,
  paramName: string
): string => {
  return (
    contact?.contact_params?.find((param) => param.param_name === paramName)
      ?.param_value ?? ""
  );
};

const resolveLeadFirstName: LeadContactHandler = ({ extraData }) => {
  return extraData?.leadContact?.first_name ?? "";
};

const resolveLeadLastName: LeadContactHandler = ({ extraData }) => {
  return extraData?.leadContact?.last_name ?? "";
};

const resolveLeadFullName: LeadContactHandler = ({ extraData }) => {
  const contact = extraData?.leadContact;
  return [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");
};

const resolveLeadBirthdate: LeadContactHandler = ({ extraData }) => {
  return getFormattedDate(extraData?.leadContact?.birthdate) ?? "";
};

const resolveLeadEmail: LeadContactHandler = ({ extraData }) => {
  return extraData?.leadContact?.active_email?.email ?? "";
};

const resolveLeadPhone: LeadContactHandler = ({ extraData }) => {
  const phone = extraData?.leadContact?.active_phone?.phone_number;
  return phone ? formatPhoneNumber(phone) : "";
};

const resolveLeadZipCode: LeadContactHandler = ({ extraData }) => {
  return extraData?.leadContact?.active_address?.zip_code ?? "";
};

const resolveLeadServiceInterests: LeadContactHandler = ({ extraData }) => {
  return (
    extraData?.leadContact?.service_interests
      ?.map((item: { name?: string }) => item.name)
      ?.filter(Boolean)
      ?.join(", ") ?? ""
  );
};

const resolvePageUrl: LeadContactHandler = ({ extraData }) => {
  return getLeadContactParam(extraData?.leadContact, "page_url");
};

const resolveFormTitle: LeadContactHandler = ({ extraData }) => {
  return getLeadContactParam(extraData?.leadContact, "form_title");
};

const resolveLeadLocationName: LeadContactHandler = ({ extraData }) => {
  return extraData?.leadContact?.location?.name ?? "";
};

const resolveLeadLocationPhone: LeadContactHandler = ({ extraData }) => {
  const phone = extraData?.leadContact?.location?.voip_number;
  return phone ? formatPhoneNumber(phone) : "";
};

const resolveLeadPreferredChannel: LeadContactHandler = ({ extraData }) => {
  return PREFERRED_CHANNEL_TYPES.getFromCode(
    extraData?.leadContact?.preferred_channel
  );
};

const LEAD_MERGE_TAG_RESOLVERS: Array<{
  key: string;
  resolve: LeadContactHandler;
}> = [
  { key: LEAD_MERGE_TAG_KEYS.LEAD_FIRST_NAME, resolve: resolveLeadFirstName },
  { key: LEAD_MERGE_TAG_KEYS.LEAD_LAST_NAME, resolve: resolveLeadLastName },
  { key: LEAD_MERGE_TAG_KEYS.LEAD_FULL_NAME, resolve: resolveLeadFullName },
  { key: LEAD_MERGE_TAG_KEYS.LEAD_BIRTHDATE, resolve: resolveLeadBirthdate },
  { key: LEAD_MERGE_TAG_KEYS.LEAD_EMAIL, resolve: resolveLeadEmail },
  { key: LEAD_MERGE_TAG_KEYS.LEAD_PHONE, resolve: resolveLeadPhone },
  { key: LEAD_MERGE_TAG_KEYS.LEAD_ZIP_CODE, resolve: resolveLeadZipCode },
  {
    key: LEAD_MERGE_TAG_KEYS.LEAD_SERVICE_INTERESTS,
    resolve: resolveLeadServiceInterests
  },
  { key: LEAD_MERGE_TAG_KEYS.PAGE_URL, resolve: resolvePageUrl },
  { key: LEAD_MERGE_TAG_KEYS.FORM_TITLE, resolve: resolveFormTitle }
];

/** Resolved lead merge-tag values keyed by canonical snake_case token name. */
export const getLeadMergeTagValuesMap = (
  leadContact?: ContactFull
): Record<string, string> => {
  const extraData = { leadContact };

  return LEAD_MERGE_TAG_RESOLVERS.reduce<Record<string, string>>(
    (acc, { key, resolve }) => {
      acc[key] = resolve({ extraData, data: {}, allowHtml: false, id: undefined }) ?? "";
      return acc;
    },
    {}
  );
};

export const LEAD_CONTACT_VALUES: MergeTagsReplaceHandler = {
  [LEAD_MERGE_TAG_KEYS.LEAD_FIRST_NAME]: resolveLeadFirstName,
  [LEAD_MERGE_TAG_KEYS.LEAD_LAST_NAME]: resolveLeadLastName,
  [LEAD_MERGE_TAG_KEYS.LEAD_FULL_NAME]: resolveLeadFullName,
  [LEAD_MERGE_TAG_KEYS.LEAD_BIRTHDATE]: resolveLeadBirthdate,
  [LEAD_MERGE_TAG_KEYS.LEAD_EMAIL]: resolveLeadEmail,
  [LEAD_MERGE_TAG_KEYS.LEAD_PHONE]: resolveLeadPhone,
  [LEAD_MERGE_TAG_KEYS.LEAD_ZIP_CODE]: resolveLeadZipCode,
  [LEAD_MERGE_TAG_KEYS.LEAD_SERVICE_INTERESTS]: resolveLeadServiceInterests,
  [LEAD_MERGE_TAG_KEYS.PAGE_URL]: resolvePageUrl,
  [LEAD_MERGE_TAG_KEYS.FORM_TITLE]: resolveFormTitle,

  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_FIRST_NAME]: resolveLeadFirstName,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_LAST_NAME]: resolveLeadLastName,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_FULL_NAME]: resolveLeadFullName,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_PHONE]: resolveLeadPhone,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_EMAIL]: resolveLeadEmail,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_SERVICE_INTERESTS]:
    resolveLeadServiceInterests,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_LOCATION_NAME]: resolveLeadLocationName,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_LOCATION_PHONE]: resolveLeadLocationPhone,
  [LEGACY_LEAD_CONTACT_OPTIONS.LEAD_PREFERRED_CHANNEL]:
    resolveLeadPreferredChannel
};
